import { shouldShowSpiceLevel, SPICE_LEVELS } from './spiceUtils';
import { calculateTotalPrice, formatPrice, parsePrice } from './priceUtils';

// Cart interfaces with sub-option support
interface CartModifierOption {
  id?: string | number;
  name: string;
  price: string | number;
  isEnabled?: boolean;
  subOption?: {
    selection: string;
    price: string;
  };
}

interface CartModifier {
  name: string;
  options: CartModifierOption[];
}

/**
 * Escape special characters and normalize strings for collision-resistant hashing
 */
const escape = (value: string): string =>
  value.replace(/([|:,\[\]])/g, '\\$1').trim().toLowerCase();

/**
 * Generate a hash from selected modifiers for unique cart item identification
 * Uses names with escaping, normalization for collision-resistant, deterministic hashing
 */
export const generateModifierHash = (selectedModifiers: any[]): string => {
  if (!selectedModifiers?.length) return 'no-mods';

  const normalized = selectedModifiers
    .map(mod => {
      const optionNames = (mod.options || [])
        .map((opt: any) => {
          try {
            let optionKey = escape(opt.name);
            
            // SAFE: Only enhance if ALL conditions are met
            if (opt && 
                typeof opt === 'object' && 
                opt.subOption && 
                typeof opt.subOption === 'object' && 
                opt.subOption.selection &&
                typeof opt.subOption.selection === 'string') {
              optionKey += `:${escape(opt.subOption.selection)}`;
            }
            
            return optionKey;
          } catch (error) {
            // FALLBACK: Use original logic if anything fails
            console.warn('Sub-option hash error, using fallback:', error);
            return escape(opt.name);
          }
        })
        .sort()
        .join(',');

      return `${escape(mod.name)}:[${optionNames}]`;
    })
    .sort()
    .join('|');

  // Stable hash function
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    hash = (hash << 5) - hash + normalized.charCodeAt(i);
    hash |= 0;
  }

  return Math.abs(hash).toString(36);
};

/**
 * Generate a unique cart item ID based on item ID, spice level, and modifiers
 * Deterministic - same item + mods = same ID (enables cart merging)
 */
export const generateCartItemId = (
  itemId: string,
  spiceLevel?: string,
  selectedModifiers: any[] = []
): string => {
  const spice = spiceLevel || 'none';
  const modifierHash = generateModifierHash(selectedModifiers);

  return `${itemId}_${spice}_${modifierHash}`;
};

/**
 * Create a simplified cart item with the new data structure
 */
export const createCartItem = (
  item: any,
  spiceLevel: number | null,
  selectedModifiers: any[],
  quantity: number = 1
) => {
  const spiceObject = shouldShowSpiceLevel(item) && spiceLevel !== null ? 
    SPICE_LEVELS.find(level => level.id === spiceLevel) : null;
  
  const totalPrice = calculateTotalPrice(item.price, selectedModifiers, 1);
  
  return {
    item,
    selected: {
      spice: spiceObject,
      modifiers: selectedModifiers
    },
    totalPrice,
    quantity,
    cartItemId: generateCartItemId(
      item.pk_id.toString(), 
      shouldShowSpiceLevel(item) && spiceLevel !== null ? spiceLevel.toString() : undefined, 
      selectedModifiers
    )
  };
};

/**
 * Create a cart item specifically for builder items (salads, etc.)
 * Correctly calculates total price based on quantity
 */
export const createBuilderCartItem = (
  item: any,
  spiceLevel: number | null,
  selectedModifiers: any[],
  quantity: number = 1
) => {
  const spiceObject = shouldShowSpiceLevel(item) && spiceLevel !== null ? 
    SPICE_LEVELS.find(level => level.id === spiceLevel) : null;
  
  // Calculate price per item (base price + modifiers), NOT multiplied by quantity
  const totalPrice = calculateTotalPrice(item.price, selectedModifiers, 1);
  
  return {
    item,
    selected: {
      spice: spiceObject,
      modifiers: selectedModifiers
    },
    totalPrice,
    quantity,
    cartItemId: generateCartItemId(
      item.pk_id.toString(), 
      shouldShowSpiceLevel(item) && spiceLevel !== null ? spiceLevel.toString() : undefined, 
      selectedModifiers
    )
  };
};

/**
 * Cart item display utilities
 */
export const getCartItemName = (cartItem: any) => cartItem.item.name;
export const getCartItemImage = (cartItem: any) => cartItem.item.image;
export const getCartItemSpiceLevel = (cartItem: any) => cartItem.selected.spice;
export const getCartItemModifiers = (cartItem: any) => cartItem.selected.modifiers;
export const getCartItemTotalPrice = (cartItem: any) => cartItem.totalPrice;

/**
 * Render spice level icons count
 */
export const getSpiceIconCount = (spiceObject: any) => {
  if (!spiceObject || !spiceObject.pepperCount) return 0;
  return spiceObject.pepperCount;
};

/**
 * Format modifiers for display with consistent price formatting and sub-option support
 */
export const formatModifiersForDisplay = (modifiers: any[]) => {
  // Safety checks - return empty array if invalid
  if (!modifiers || !Array.isArray(modifiers)) return [];
  
  return modifiers
    .filter(modifier => {
      // Multiple safety checks
      return modifier && 
             typeof modifier === 'object' &&
             modifier.options && 
             Array.isArray(modifier.options) &&
             modifier.options.length > 0;
    })
    .map(modifier => {
      const optionNames = modifier.options
        .filter((opt: CartModifierOption) => opt && typeof opt === 'object') // Filter out invalid options
        .map((opt: CartModifierOption) => {
          // Safe sub-option display
          if (opt.subOption && 
              typeof opt.subOption === 'object' && 
              opt.subOption.selection && 
              typeof opt.subOption.selection === 'string') {
            return `${opt.subOption.selection}:${opt.name}`;
          }
          // Fallback to option name
          return opt.name || 'Unknown Option';
        })
        .join(', ');
      
      const totalPrice = modifier.options.reduce((sum: number, opt: CartModifierOption) => {
        if (!opt || typeof opt !== 'object') return sum; // Skip invalid options
        
        // Safe price extraction
        let price = 0;
        if (opt.subOption && 
            typeof opt.subOption === 'object' && 
            opt.subOption.price) {
          price = parsePrice(opt.subOption.price) || 0;
        } else if (opt.price) {
          price = parsePrice(opt.price) || 0;
        }
        
        return sum + price;
      }, 0);
      
      return {
        name: modifier.name || 'Unknown Modifier',
        options: optionNames || 'No Options',
        price: totalPrice,
        formattedPrice: formatPrice(totalPrice)
      };
    });
};

/**
 * Format modifiers for display with quantity-based pricing and sub-option support
 */
export const formatModifiersForDisplayWithQuantity = (modifiers: any[], quantity: number) => {
  if (!modifiers || !Array.isArray(modifiers)) return [];
  
  return modifiers
    .filter(modifier => {
      // Multiple safety checks
      return modifier && 
             typeof modifier === 'object' &&
             modifier.options && 
             Array.isArray(modifier.options) &&
             modifier.options.length > 0;
    })
    .map(modifier => {
      const optionNames = modifier.options
        .filter((opt: CartModifierOption) => opt && typeof opt === 'object') // Filter out invalid options
        .map((opt: CartModifierOption) => {
          // Safe sub-option display
          if (opt.subOption && 
              typeof opt.subOption === 'object' && 
              opt.subOption.selection && 
              typeof opt.subOption.selection === 'string') {
            return `${opt.subOption.selection}:${opt.name}`;
          }
          // Fallback to option name
          return opt.name || 'Unknown Option';
        })
        .join(', ');
      
      const totalPrice = modifier.options.reduce((sum: number, opt: CartModifierOption) => {
        if (!opt || typeof opt !== 'object') return sum; // Skip invalid options
        
        // Safe price extraction with sub-option support
        let price = 0;
        if (opt.subOption && 
            typeof opt.subOption === 'object' && 
            opt.subOption.price) {
          price = parsePrice(opt.subOption.price) || 0;
        } else if (opt.price) {
          price = parsePrice(opt.price) || 0;
        }
        
        return sum + price;
      }, 0);
      
      const totalPriceWithQuantity = totalPrice * quantity;
      
      return {
        name: modifier.name || 'Unknown Modifier',
        options: optionNames || 'No Options',
        price: totalPrice,
        totalPriceWithQuantity,
        formattedPrice: formatPrice(totalPriceWithQuantity)
      };
    });
};

/**
 * Get the total cart quantity for a menu item across all variations
 * Returns sum of quantities for all cart items matching this item ID
 * (includes all variations: with/without modifiers, different spice levels)
 */
export const getCartQuantity = (
  itemId: string,
  cartItems: any[]
): number => {
  return cartItems
    .filter(cartItem => cartItem.item.pk_id.toString() === itemId)
    .reduce((total, cartItem) => total + cartItem.quantity, 0);
};

/**
 * Get the cart item ID for a simple menu item (for updateQuantity calls)
 */
export const getCartItemId = (itemId: string): string => {
  return generateCartItemId(itemId, 'none', []);
};

/**
 * Smart Cart Helper Functions for RepeatCustomisationModal
 */

/**
 * Find all cart variants of the same base item (different customizations)
 */
export const findCartItemVariants = (itemId: string, cartItems: any[]): any[] => {
  return cartItems.filter(cartItem => 
    cartItem.item.pk_id.toString() === itemId
  );
};

/**
 * Check if a cart item has any customizations (modifiers or spice level)
 */
export const hasCustomizations = (cartItem: any): boolean => {
  const hasSpice = cartItem.selected.spice !== null;
  const hasModifiers = cartItem.selected.modifiers && 
                       cartItem.selected.modifiers.length > 0;
  return hasSpice || hasModifiers;
};

/**
 * Format previous customizations for display in RepeatCustomisationModal
 */
export const formatPreviousCustomizations = (cartItem: any): string[] => {
  const selections: string[] = [];
  
  // Add spice level if present
  if (cartItem.selected.spice) {
    selections.push(`${cartItem.selected.spice.name} spice`);
  }
  
  // Add modifiers if present
  if (cartItem.selected.modifiers && cartItem.selected.modifiers.length > 0) {
    cartItem.selected.modifiers.forEach((mod: any) => {
      const options = mod.options.map((opt: any) => opt.name).join(', ');
      selections.push(options);
    });
  }
  
  return selections.length > 0 ? selections : ['No customizations'];
};
